/**
 * 🔐 Auth Service v3 — Pure Firebase Auth + Custom Claims
 *
 * المعمارية الجديدة (Production-grade):
 *   1. كل authentication عبر Firebase Auth
 *   2. الصلاحيات (role) من JWT Custom Claims (مش من Firestore)
 *   3. Firestore يستخدم فقط للبيانات الإضافية (name, phone, etc.)
 *
 * المزايا:
 *   ✅ أمان أعلى (claims signed بـ Firebase)
 *   ✅ أداء أسرع (لا Firestore reads للـ role)
 *   ✅ Standard Firebase practice
 *   ✅ يدعم Firestore Rules بـ `request.auth.token.role`
 *
 * ⚠️ ملاحظة:
 *   لو في users قديمين في Firestore بدون Firebase Auth account،
 *   لازم تشغّل migration script (tools/migrate-users-to-auth.js)
 *   اللي يضيفهم لـ Firebase Auth + يحدّد claims لهم.
 */

import {
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendPasswordResetEmail as firebaseSendPasswordReset,
  sendSignInLinkToEmail as firebaseSendSignInLink,
  isSignInWithEmailLink as firebaseIsSignInWithEmailLink,
  signInWithEmailLink as firebaseSignInWithEmailLink,
  updatePassword as firebaseUpdatePassword,
  type User as FirebaseUser,
  type Unsubscribe,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

import { auth, db } from '@infrastructure/firebase/config';
import {
  useAuthStore,
  type AuthUser,
  type UserRole,
} from '@application/stores/auth.store';
import { UnauthorizedError } from '@domain/errors/DomainErrors';

// ═══════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════
interface CustomClaims {
  role?: string;
  // panel: في الـ admin claims يكون boolean=true
  //         في الـ provider claims يكون string='provider'
  panel?: boolean | string;
  providerId?: string;
  isSuperAdmin?: boolean;
  admin?: boolean;
  vendor?: boolean;
  permissions?: string[];
}

// ═══════════════════════════════════════════════════
// Service
// ═══════════════════════════════════════════════════
class AuthService {
  /**
   * 🔐 تسجيل دخول
   *
   * يعتمد على Firebase Auth فقط — لا Firestore fallback.
   */
  async signInWithEmail(email: string, password: string): Promise<AuthUser> {
    const normalizedEmail = email.toLowerCase().trim();

    // 🛡️ (M-6 fix) Dev-only logs — emails ما تظهر في production console
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log('[Auth] محاولة تسجيل دخول:', normalizedEmail);
    }

    try {
      const credential = await signInWithEmailAndPassword(
        auth,
        normalizedEmail,
        password
      );

      const user = await this.buildAuthUser(credential.user);
      // 🛡️ (M-6 fix) Dev-only — role ما يظهر في production
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log('[Auth] ✅ تسجيل دخول ناجح', { role: user.role });
      }

      useAuthStore.getState().setUser(user);
      return user;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(
        '[Auth] ❌ فشل تسجيل الدخول:',
        (err as { code?: string })?.code ?? 'unknown'
      );
      throw this.handleAuthError(err);
    }
  }

  /**
   * 🚪 تسجيل خروج
   */
  async signOut(): Promise<void> {
    await firebaseSignOut(auth);
    useAuthStore.getState().logout();
  }

  /**
   * 📧 إرسال إيميل إعادة تعيين كلمة السر
   *
   * يستخدم Firebase Auth لإرسال link لإعادة التعيين.
   * Firebase تتعامل مع الـ token + الـ flow كله.
   *
   * ⚠️ لأسباب أمنية: حتى لو الإيميل ما موجود، نعرض رسالة نجاح
   *    (عشان ما نكشف هل الإيميل مسجّل أو لا)
   */
  async sendPasswordResetEmail(email: string): Promise<void> {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      throw new Error('الرجاء إدخال البريد الإلكتروني');
    }

    // Email validation بسيط
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) {
      throw new Error('البريد الإلكتروني غير صالح');
    }

    try {
      await firebaseSendPasswordReset(auth, trimmed, {
        // 🔗 الرابط اللي يرجع له المستخدم بعد reset
        url: `${window.location.origin}/login`,
        handleCodeInApp: false, // Firebase يستضيف صفحة الـ reset
      });
    } catch (err: unknown) {
      const firebaseError = err as { code?: string; message?: string };

      // 🛡️ نتعامل مع الأخطاء بطريقة لا تكشف معلومات حساسة
      switch (firebaseError.code) {
        case 'auth/invalid-email':
          throw new Error('البريد الإلكتروني غير صالح');
        case 'auth/user-not-found':
          // ⚠️ نخفي هذا الخطأ — نعرض نجاح عشان الأمان
          // eslint-disable-next-line no-console
          console.warn('[Auth] محاولة reset لإيميل غير مسجّل:', trimmed);
          return; // نرجع كأنه نجح
        case 'auth/too-many-requests':
          throw new Error(
            'محاولات كثيرة — انتظر دقائق ثم حاول مرة أخرى'
          );
        case 'auth/network-request-failed':
          throw new Error('فشل الاتصال — تحقق من الإنترنت');
        default:
          // eslint-disable-next-line no-console
          console.error('[Auth] resetPassword error:', firebaseError);
          throw new Error('حدث خطأ، حاول مرة أخرى');
      }
    }
  }

  /**
   * 📧 (Provider onboarding) إرسال رابط تفعيل الحساب إلى بريد المالك
   *
   * يُستدعى بعد نجاح إنشاء المزود. يستخدم Firebase email‑link (passwordless)
   * المجاني الرسمي: يُرسِل رابطاً يفتح صفحة `/auth/action` **داخل التطبيق**
   * مباشرة (لأن `handleCodeInApp: true` يحترم الـ `url` الذي نمرّره) — بدون
   * المرور على صفحة Firebase المستضافة `/__/auth/action`.
   *
   * نمرّر البريد كـ query param لأن الأدمن يرسل الرابط من جهازه بينما يفتحه
   * المالك على جهاز آخر (localStorage لا يُشارك بين الجهازين).
   *
   * متطلّب console (لمرة واحدة): تفعيل "Email link (passwordless sign‑in)"
   *   في Authentication → Sign‑in method → Email/Password.
   */
  async sendProviderSetPasswordEmail(email: string): Promise<void> {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      throw new Error('بريد المالك مفقود');
    }
    try {
      await firebaseSendSignInLink(auth, trimmed, {
        url: `${window.location.origin}/auth/action?email=${encodeURIComponent(
          trimmed
        )}`,
        handleCodeInApp: true,
      });
    } catch (err: unknown) {
      const code = (err as { code?: string }).code;
      if (code === 'auth/too-many-requests') {
        throw new Error('محاولات كثيرة — انتظر دقائق ثم أعد الإرسال');
      }
      if (code === 'auth/operation-not-allowed') {
        throw new Error(
          'فعّل "Email link (passwordless sign‑in)" في إعدادات Firebase أولاً'
        );
      }
      // eslint-disable-next-line no-console
      console.error('[Auth] sendProviderSetPasswordEmail error:', code);
      throw new Error('تعذّر إرسال رابط التفعيل. حاول مرة أخرى.');
    }
  }

  /** هل الرابط الحالي هو رابط email‑link صالح للدخول؟ */
  isEmailSignInLink(href: string): boolean {
    return firebaseIsSignInWithEmailLink(auth, href);
  }

  /**
   * 🔐 (Provider onboarding) إكمال تفعيل الحساب عبر رابط الإيميل:
   *   1. تسجيل الدخول عبر email‑link (يثبت ملكية البريد ويجعله verified)
   *   2. تعيين كلمة السر التي اختارها المالك (updatePassword)
   * ثم يُبنى AuthUser ويوضع في الـ store. يرمي عند بطلان/انتهاء الرابط.
   */
  async completeProviderEmailLinkSetup(
    email: string,
    href: string,
    newPassword: string
  ): Promise<AuthUser> {
    const credential = await firebaseSignInWithEmailLink(auth, email, href);
    await firebaseUpdatePassword(credential.user, newPassword);
    const user = await this.buildAuthUser(credential.user);
    useAuthStore.getState().setUser(user);
    return user;
  }

  /**
   * 🔄 Listen للتغيرات في حالة المصادقة
   */
  onAuthStateChange(): Unsubscribe {
    return onAuthStateChanged(auth, async (firebaseUser) => {
      const store = useAuthStore.getState();

      if (firebaseUser) {
        try {
          const user = await this.buildAuthUser(firebaseUser);
          store.setUser(user);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('[Auth] فشل بناء AuthUser:', err);
          store.logout();
        }
      } else {
        store.logout();
      }

      store.setLoading(false);
    });
  }

  /**
   * 🔄 Force refresh الـ token (لو Custom Claims اتحدّثت)
   *
   * استخدم هذا بعد ما الـ backend يضيف claims جديدة.
   */
  async refreshToken(): Promise<void> {
    const fbUser = auth.currentUser;
    if (!fbUser) return;

    // force refresh
    await fbUser.getIdToken(true);

    // أعد بناء الـ user في الـ store
    const user = await this.buildAuthUser(fbUser);
    useAuthStore.getState().setUser(user);
  }

  // ═══════════════════════════════════════════════════
  // Private
  // ═══════════════════════════════════════════════════

  /**
   * 🏗️ بناء AuthUser من Firebase user
   *
   * Strategy:
   *   1. اقرأ Custom Claims من JWT → role
   *   2. اقرأ بيانات إضافية من Firestore (name, phone) — اختياري
   */
  private async buildAuthUser(fbUser: FirebaseUser): Promise<AuthUser> {
    // 1️⃣ اقرأ Custom Claims
    const claims = await this.getClaims(fbUser);

    // 2️⃣ حدّد الـ role من claims
    const role = this.deriveRole(claims);

    // 🛡️ (M-6 fix) Dev-only — claims sensitive ما تظهر في production
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log('[Auth] 🎫 Token claims:', { role });
    }

    // 3️⃣ بيانات إضافية من Firestore (best-effort)
    const profile = await this.getProfile(fbUser, role);

    // 🆕 (Phase 4) جلب mustChangePassword + permissions للـ provider users
    const isProviderUser =
      role === 'provider_owner' ||
      role === 'provider_manager' ||
      role === 'booking_staff' ||
      role === 'accountant' ||
      role === 'viewer';

    let mustChangePassword = false;
    let permissions: string[] = [];
    if (isProviderUser) {
      try {
        const puSnap = await getDoc(doc(db, 'providerUsers', fbUser.uid));
        if (puSnap.exists()) {
          const data = puSnap.data();
          mustChangePassword = data['mustChangePassword'] === true;
          permissions = (data['permissions'] as string[]) ?? [];
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[Auth] فشل قراءة providerUser:', err);
      }
    }

    const providerId = claims.providerId as string | undefined;

    return {
      uid: fbUser.uid,
      email: fbUser.email,
      phone: fbUser.phoneNumber ?? profile.phone ?? null,
      displayName:
        fbUser.displayName ?? profile.name ?? this.fallbackName(fbUser, role),
      role,
      emailVerified: fbUser.emailVerified,
      ...(fbUser.photoURL ? { photoURL: fbUser.photoURL } : {}),
      ...(isProviderUser ? { mustChangePassword } : {}),
      ...(providerId ? { providerId } : {}),
      ...(isProviderUser ? { permissions } : {}),
    };
  }

  /**
   * 🎫 جلب Custom Claims من JWT
   */
  private async getClaims(fbUser: FirebaseUser): Promise<CustomClaims> {
    try {
      const tokenResult = await fbUser.getIdTokenResult();
      return tokenResult.claims as CustomClaims;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[Auth] فشل قراءة claims:', err);
      return {};
    }
  }

  /**
   * 🛡️ تحديد الـ role من claims
   *
   * Priority:
   *   1. claims.role (explicit)
   *   2. claims.isSuperAdmin → 'admin'
   *   3. claims.panel → 'admin'
   *   4. claims.admin → 'admin'
   *   5. claims.vendor → 'vendor'
   *   6. default → 'vendor'
   */
  private deriveRole(claims: CustomClaims): UserRole {
    // 🆕 (Phase 4) Provider panel — أعلى أولوية
    //   لو panel === 'provider' (string)، نستخدم claims.role
    if (claims.panel === 'provider') {
      const providerRole = claims.role as UserRole;
      if (
        providerRole === 'provider_owner' ||
        providerRole === 'provider_manager' ||
        providerRole === 'booking_staff' ||
        providerRole === 'accountant' ||
        providerRole === 'viewer'
      ) {
        return providerRole;
      }
      // fallback لو الـ role غير معروف لكن panel='provider'
      return 'provider_owner';
    }

    // explicit role (admin)
    //   ⚠️ نميّز super_admin عن admin — بعض الميزات (سجل التدقيق) مقصورة
    //   على super_admin، والـ Firestore rules تفحص token.role == 'superAdmin'.
    //   قيمة الـ claim من الـ backend camelCase ('superAdmin')؛ نطبّعها
    //   لصيغة النوع الداخلية ('super_admin').
    if (claims.role === 'superAdmin' || claims.role === 'super_admin') {
      return 'super_admin';
    }
    if (claims.role === 'admin') {
      return 'admin';
    }
    if (claims.role === 'vendor' || claims.role === 'customer') {
      return claims.role as UserRole;
    }

    // 🛡️ (H-11 fix) boolean flags — strict === true check
    //   كان claims.isSuperAdmin و claims.vendor يستخدمون truthy check،
    //   اللي يقدر يقبل قيم غريبة (string "false" مثلاً) كـ admin/vendor.
    //   الآن نتأكد من boolean true بدقة.
    if (claims.isSuperAdmin === true) {
      return 'super_admin';
    }
    if (claims.panel === true || claims.admin === true) {
      return 'admin';
    }
    if (claims.vendor === true) {
      return 'vendor';
    }

    // default
    return 'vendor';
  }

  /**
   * 📋 جلب بيانات الملف الشخصي من Firestore (best-effort)
   *
   * نقرأ من userspanel للـ admins، من users للباقي.
   * هذي بيانات إضافية فقط — مش للأمان.
   */
  private async getProfile(
    fbUser: FirebaseUser,
    role: UserRole
  ): Promise<{ name?: string; phone?: string }> {
    // اختر المجموعة المناسبة حسب الـ role
    const isProviderUser =
      role === 'provider_owner' ||
      role === 'provider_manager' ||
      role === 'booking_staff' ||
      role === 'accountant' ||
      role === 'viewer';

    const collection = isProviderUser
      ? 'providerUsers'
      : role === 'admin' || role === 'super_admin'
        ? 'userspanel'
        : 'customers';

    try {
      const snap = await getDoc(doc(db, collection, fbUser.uid));
      if (!snap.exists()) return {};

      const data = snap.data();
      return {
        name:
          (data['fullName'] as string) ??
          (data['name'] as string) ??
          (data['displayName'] as string),
        phone: data['phone'] as string,
      };
    } catch {
      return {};
    }
  }

  /**
   * 🎨 اسم افتراضي حسب الـ role
   */
  private fallbackName(fbUser: FirebaseUser, role: UserRole): string {
    if (role === 'super_admin') return 'مدير عام';
    if (role === 'admin') return 'مدير';
    if (role === 'vendor') return fbUser.email?.split('@')[0] ?? 'تاجر';
    if (role === 'provider_owner') return 'مالك المؤسسة';
    if (role === 'provider_manager') return 'مدير المؤسسة';
    if (role === 'booking_staff') return 'موظف حجوزات';
    if (role === 'accountant') return 'محاسب';
    if (role === 'viewer') return 'مشاهد';
    return 'مستخدم';
  }

  /**
   * 🚨 معالجة أخطاء Firebase Auth
   */
  private handleAuthError(error: unknown): Error {
    const code = (error as { code?: string })?.code ?? '';

    const messages: Record<string, string> = {
      'auth/invalid-email': 'البريد الإلكتروني غير صالح',
      'auth/user-disabled': 'الحساب معطّل. تواصل مع الدعم.',
      'auth/user-not-found': 'بيانات تسجيل الدخول غير صحيحة',
      'auth/wrong-password': 'بيانات تسجيل الدخول غير صحيحة',
      'auth/invalid-credential': 'بيانات تسجيل الدخول غير صحيحة',
      'auth/too-many-requests': 'محاولات كثيرة. حاول بعد قليل.',
      'auth/network-request-failed': 'تعذّر الاتصال بالشبكة',
      'auth/configuration-not-found':
        'Email/Password غير مفعّل في Firebase Console.',
    };

    const message = messages[code] ?? 'فشل تسجيل الدخول. حاول مرة أخرى.';
    return new UnauthorizedError(message, { code });
  }
}

export const authService = new AuthService();
